import summaryCommonStore from "@/store/summaryCommon.store";
import { DetailErrorType } from "@/constants/record";
import Header from "./components/Header";
import "./index.less";
import LinkView from "./components/LinkView";
import SpeechDraftView from "./components/SpeechDraftView";
import IC_EXPAND from "@/assets/simulInter/ic_expand.svg?react";
import { useEffect, useRef, useState, useCallback } from "react";
import { v4 as uuidV4 } from "uuid";
import CollaborationView from "./components/CollaborationView";
import ControlStartView from "./components/ControlStartView";
import { controlStartedBoard } from "@/services/simulInter";
import { ControlHandle, ControllerStatus } from "@/types/simulInter";
import CommonDialog, { CommonDialogRef } from "./components/CommonDialog";
import IPConnectModal, { IPConnectModalRef } from "./components/LinkView/components/IPConnectModal";
import CodeConnectModal, { CodeConnectModalRef } from "./components/LinkView/components/ConnectModal";
import { useControlWebSocket } from "@/hooks/useControlWebSocket";
import deviceStore from "@/store/device.store";
import { message } from "antd";
// import { renameRecord } from "@/services/summary";
import { MessageType } from "antd/es/message/interface";
import { useNavigate } from "react-router-dom";

enum ConnectorState {
  Init = "init",
  UnStart = "UnStart",
  Recording = "Recording",
}

const SimulInter = () => {
  const navigate = useNavigate();
  const errorStatus = summaryCommonStore((state) => state.errorStatus);
  const [showDraftView, setShowDraftView] = useState(true);
  const [originStreamName, setOriginStreamName] = useState("");
  const [createdDate, setCreatedDate] = useState("");
  const [title, setTitle] = useState("");
  const [connectState, setConnectState] = useState(ConnectorState.Init);
  const [streamName, setStreamName] = useState("");
  const [deviceSn, setDeviceSn] = useState<string>("");
  const [expectedBoardSn, setExpectedBoardSn] = useState<string>(""); // 从列表进入时的预期设备SN
  const [isFromList, setIsFromList] = useState(false); // 是否从列表进入
  const [controllerStatus, setControllerStatus] = useState<ControllerStatus | null>(null); // 控制器状态
  const commonDialogRef = useRef<CommonDialogRef>(null);
  const ipConnectModalRef = useRef<IPConnectModalRef>(null);
  const codeConnectModalRef = useRef<CodeConnectModalRef>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const socketDisconnectToastCancelRef = useRef<MessageType | null>(null);

  const socketDisconnectToastElement = () => {
    return <div>连接异常断开，<span className="text-#4981F2 cursor-pointer" onClick={handleConnectByCode}>重新连接</span></div>;
  }

  // Dialog 显示辅助函数
  const showRebootDialog = () => {
    commonDialogRef.current?.show({
      title: "无法连接设备",
      content: "设备已有录制进行中，是否结束设备当前的录制进程？",
      buttons: [
        {
          text: "取消",
          type: "secondary",
          onClick: () => { }
        },
        {
          text: "结束设备录制，并连接",
          type: "primary",
          onClick: handleEndRecording
        }
      ]
    });
  };

  const showJoinDialog = (targetStreamName: string) => {
    commonDialogRef.current?.show({
      title: "无法控制设备",
      content: "设备已有管理员控制中，点击下方按钮立即加入，或回到首页使用【连接加入】功能",
      buttons: [
        {
          text: "知道了",
          type: "secondary",
          onClick: () => { }
        },
        {
          text: "立即加入",
          type: "primary",
          onClick: () => {
            if (targetStreamName) {
              navigate({
                pathname: "/collaboration",
                search: `?streamId=${targetStreamName.replace("live/", "")}`,
              });
            }
          }
        }
      ]
    });
  };

  const showConflictDialog = () => {
    commonDialogRef.current?.show({
      title: "无法开启同步",
      content: "设备当前的录制信息，您已有记录在控制，请从列表点击进入",
      buttons: [
        {
          text: "取消",
          type: "secondary",
          onClick: () => { }
        },
        {
          text: "知道了",
          type: "primary",
          onClick: () => navigate("/home")
        }
      ]
    });
  };

  const showMismatchDialog = () => {
    commonDialogRef.current?.show({
      title: "设备不一致",
      content: "当前连接的设备，与本记录控制的设备不一致",
      buttons: [
        {
          text: "知道了",
          type: "primary",
          onClick: () => { }
        }
      ]
    });
  };

  // WebSocket 连接管理 (单例模式)
  const {
    connect: connectWebSocket,
    disconnect: disconnectWebSocket,
    stopRecording: wsStopRecording,
  } = useControlWebSocket({
    onConnected: () => {
      console.log('SimulInter WebSocket 连接成功');
      socketDisconnectToastCancelRef.current?.();
      ipConnectModalRef.current?.close();
      codeConnectModalRef.current?.close();
    },
    onErrorDisconnect: () => {
      console.log('SimulInter WebSocket 连接断开');
      socketDisconnectToastCancelRef.current = message.warning(socketDisconnectToastElement(), 0);
    }
  });

  const handleConnectByCode = () => {
    ipConnectModalRef.current?.close();
    codeConnectModalRef.current?.open();
  };

  const handleConnectByIp = () => {
    codeConnectModalRef.current?.close();
    ipConnectModalRef.current?.open();
  };

  // 清理资源的公共函数
  const cleanupResources = useCallback(() => {
    console.log("清理资源：关闭Toast、断开WebSocket、重置设备状态");
    socketDisconnectToastCancelRef.current?.();
    socketDisconnectToastCancelRef.current = null;
    disconnectWebSocket();
    deviceStore.getState().resetStore();
  }, [disconnectWebSocket]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const streamName =
      queryParams.get("streamName") || `live/${uuidV4().replaceAll("-", "")}`;
    const createdDate = queryParams.get("createdDate") || "";
    const titleFromUrl = queryParams.get("title") || "";
    const boardSn = queryParams.get("boardSn") || ""; // 从列表进入时的设备SN
    const isFromListParam = queryParams.get("isFromList") === "true"; // 是否从列表进入
    const controllerStatusParam = queryParams.get("controllerStatus"); // 控制器状态

    setCreatedDate(createdDate);
    setTitle(titleFromUrl);
    setIsFromList(isFromListParam);
    if (controllerStatusParam) {
      setControllerStatus(Number(controllerStatusParam) as ControllerStatus);
    }
    if (streamName) {
      console.log("simulInter streamName", streamName);
      setOriginStreamName(`${streamName}`);
    }
    if (boardSn) {
      console.log("simulInter expectedBoardSn (from list)", boardSn);
      setExpectedBoardSn(boardSn);
    }

    // 如果是录制中从列表进入，需要先显示字幕同步页面，然后弹出连接码弹窗
    if (isFromListParam && controllerStatusParam && Number(controllerStatusParam) === ControllerStatus.RECORDING) {
      console.log("录制中从列表进入，先显示字幕同步页面");
      setStreamName(streamName);
      setConnectState(ConnectorState.Recording);
      // 延迟弹出连接码弹窗，确保页面已渲染
      setTimeout(() => {
        codeConnectModalRef.current?.open();
      }, 100);
    }

    socketDisconnectToastCancelRef.current?.();
    socketDisconnectToastCancelRef.current = null;

    // 组件卸载时的清理函数
    return () => {
      cleanupResources();
    };
  }, [cleanupResources]);

  useEffect(() => {
    if (connectState === ConnectorState.Init) {
      cleanupResources();
    }
  }, [connectState, cleanupResources])

  const handleToggleSpeechDraft = () => {
    console.log("handleShowSpeechDraft");
    setShowDraftView(!showDraftView);
  };

  // 处理录制中进入时的fetchStreamName逻辑
  const fetchStreamNameForRecording = async (boardStreamName: string): Promise<boolean> => {
    if (!originStreamName || !boardStreamName) {
      return false;
    }

    try {
      // 获取设备SN
      const boardSn = await deviceStore.getState().fetchDeviceSn();
      setDeviceSn(boardSn);

      const res = await controlStartedBoard({
        originStreamName,
        boardStreamName,
        controllerStatus: ControllerStatus.RECORDING,
        boardSn: boardSn || undefined,
      });
      const { streamName, controlHandle } = res.data;

      // 录制中进入时，只需判断LINk_JOIN
      if (controlHandle === ControlHandle.LINk_JOIN) {
        // 如果streamName一致，则正常进入
        if (originStreamName === streamName) {
          setStreamName(streamName);
          const success = await startBoardSocket();
          if (!success) {
            return false;
          }
          setConnectState(ConnectorState.Recording);
          return true;
        } else {
          // streamName不一致，提示错误
          message.error("大屏当前的录制信息，与本场记录不一致");
          return false;
        }
      } else {
        // 不是LINk_JOIN，提示错误
        message.error("大屏当前的录制信息，与本场记录不一致");
        return false;
      }
    } catch (error: any) {
      console.error("controlStartedBoard 失败:", error);
      message.error(error?.errMsg || error?.message || '连接大屏失败');
      return false;
    }
  };

  // 处理录制中进入的连接成功逻辑
  const handleConnectSuccessForRecording = async (isStart: boolean, boardStreamName?: string) => {
    await deviceStore.getState().fetchAndStoreDeviceName();

    // 连接成功后获取设备SN并校验
    const actualSn = await deviceStore.getState().fetchDeviceSn();
    setDeviceSn(actualSn);

    // 录制中进入，sn不一样
    if (!validateDeviceSn(actualSn)) {
      message.error("当前连接的设备，与本记录控制的设备不一致");
      // 断开当前连接，但不改变状态，让用户重新连接
      disconnectWebSocket();
      return;
    }

    // 录制中进入，!isStart是有问题的，直接toast
    if (!isStart) {
      message.error("当前大屏没有正常开启录制，请前往大屏处理");
      return;
    }

    // 已启动，调用录制控制接口
    if (isStart && boardStreamName) {
      const success = await fetchStreamNameForRecording(boardStreamName);
      // 只有验证通过后，才关闭弹框
      if (success) {
        ipConnectModalRef.current?.close();
        codeConnectModalRef.current?.close();
      }
    }
  };

  // 处理普通进入时的fetchStreamName逻辑
  const fetchStreamName = async (boardStreamName: string) => {
    if (!originStreamName || !boardStreamName) {
      return;
    }
    setLinkLoading(true);

    try {
      // 获取设备SN
      const boardSn = await deviceStore.getState().fetchDeviceSn();
      setDeviceSn(boardSn);

      const res = await controlStartedBoard({
        originStreamName,
        boardStreamName,
        controllerStatus: ControllerStatus.RECORDING,
        boardSn: boardSn || undefined, // 只在有值时传递
      });
      const { streamName, controlHandle } = res.data;
      setStreamName(streamName);
      setLinkLoading(false);
      if (controlHandle === ControlHandle.LINk_JOIN) {
        // 可以控制，但是通过控制加入，或者跟大板streamName不一致，显示冲突提示
        if (!isFromList || originStreamName !== streamName) {
          showConflictDialog();
          return;
        }

        const success = await startBoardSocket();
        if (!success) {
          return;
        }
        setConnectState(ConnectorState.Recording);
      }
      else if (controlHandle === ControlHandle.REBOOT) {
        // 重启，展示重启弹窗
        const success = await startBoardSocket();
        if (!success) {
          return;
        }
        showRebootDialog();
      } else if (controlHandle === ControlHandle.TIPS_JOIN) {
        showJoinDialog(streamName);
      }
    } catch (error: any) {
      setLinkLoading(false);
      console.error("controlStartedBoard 失败:", error);
      // 其他错误正常提示
      message.error(error?.errMsg || error?.message || '连接设备失败');
    }
  };

  // 处理普通进入的连接成功逻辑
  const handleConnectSuccess = async (isStart: boolean, boardStreamName?: string) => {
    // 先关闭弹框
    ipConnectModalRef.current?.close();
    codeConnectModalRef.current?.close();

    await deviceStore.getState().fetchAndStoreDeviceName();

    // 连接成功后获取设备SN并校验
    const actualSn = await deviceStore.getState().fetchDeviceSn();
    setDeviceSn(actualSn);

    if (isFromList && !validateDeviceSn(actualSn)) {
      // 如果从列表进入，需要校验设备SN是否一致
      showMismatchDialog();
      disconnectWebSocket();
      setConnectState(ConnectorState.Init);
      return;
    }

    // 未启动，跳转到启动页
    if (!isStart) {
      const success = await startBoardSocket();
      if (!success) {
        return;
      }
      setConnectState(ConnectorState.UnStart);
      return;
    }
    // 已启动，调用录制控制接口
    if (isStart && boardStreamName) {
      fetchStreamName(boardStreamName);
    }
  };

  // 处理连接成功（根据是否录制中进入选择不同的处理逻辑）
  const handleConnectSuccessWrapper = async (isStart: boolean, boardStreamName?: string) => {
    // 如果是录制中从列表进入，使用特殊处理逻辑
    if (isFromList && controllerStatus === ControllerStatus.RECORDING) {
      await handleConnectSuccessForRecording(isStart, boardStreamName);
    } else {
      await handleConnectSuccess(isStart, boardStreamName);
    }
  };

  // 处理弹窗关闭
  const handleModalClose = (needToHome: boolean) => {
    if (isFromList && controllerStatus === ControllerStatus.RECORDING && needToHome) {
      navigate("/home");
    }
  };

  // 处理 showJumpSummaryDialog 显示时的逻辑
  const handleShowJumpSummaryDialog = (show: boolean) => {
    // 如果是列表进入且录制中，当显示跳转总结弹框时，关闭连接弹框
    if (show && isFromList && controllerStatus === ControllerStatus.RECORDING) {
      console.log("会议已结束，关闭连接弹框");
      ipConnectModalRef.current?.close();
      codeConnectModalRef.current?.close();
    }
  };

  // 验证设备SN是否一致（从列表进入时）
  const validateDeviceSn = (actualSn: string): boolean => {
    if (!expectedBoardSn) {
      return true;
    }

    // 对比实际SN和预期SN
    if (actualSn !== expectedBoardSn) {
      console.warn('[ControlWS] 设备SN不匹配：', {
        expected: expectedBoardSn,
        actual: actualSn
      });
      return false;
    }

    return true;
  };

  // 连接成功后建立 WebSocket 连接
  const startBoardSocket = async (): Promise<boolean> => {
    const { connectionInfo } = deviceStore.getState();
    if (connectionInfo) {
      console.log("建立 WebSocket 连接：", connectionInfo);
      try {
        await connectWebSocket(connectionInfo.deviceIP, connectionInfo.serverPort);
        return true;
      } catch (error) {
        message.error('连接设备失败');
        return false;
      }
    } else {
      message.error('连接信息不存在，无法与设备建立连接');
      console.error("连接信息不存在，无法与设备建立连接");
      return false;
    }
  };

  const handleBack = () => {
    // 用户主动点击返回按钮时，立即清理资源
    cleanupResources();
  };

  const handleStartRecordSuccess = (streamName: string) => {
    setConnectState(ConnectorState.Recording);
    setStreamName(streamName);
  };

  // 结束设备录制，并连接
  const handleEndRecording = async () => {
    console.log("结束设备录制，并重新连接");

    const success = wsStopRecording();
    if (!success) {
      message.error("发送结束录制指令失败");
      return;
    }
    await new Promise(resolve => setTimeout(resolve, 1000));
    setConnectState(ConnectorState.UnStart);
  };

  // const handleCommitTitleChange = async (title: string): Promise<boolean> => {
  //   console.log("handleCommitTitleChange", title);

  //   try {
  //     await renameRecord(`${originStreamName.startsWith("live/") ? originStreamName : `live/${originStreamName}`}`, title);
  //     return true;
  //   } catch (error) {
  //     console.error(error);
  //     message.error("修改标题失败");
  //     return false;
  //   }
  // }

  return (
    <div className="w-full h-full flex flex-col">
      <Header
        handleBack={handleBack}
        title={title}
        // 交互：本期未总结完成的，不可修改标题
        titleEditable={false}
        isOnlyBack={
          errorStatus === DetailErrorType.deleted ||
          errorStatus === DetailErrorType.ERR_NETWORK
        }
        showSetting={connectState === ConnectorState.Recording}
        createdDate={createdDate}
      />
      <div className="flex flex-1 m-4 mt-0 overflow-hidden relative">
        {!showDraftView && (
          <div
            className="absolute z-1 left-0 top-8 h-10 flex items-center justify-center bg-#fff pl-2.5 pr-4.5 bullet-tag cursor-pointer"
            onClick={handleToggleSpeechDraft}
          >
            <IC_EXPAND className="w-3.5 h-3.5 mr-1" />
            <div className="text-3.5 text-mx-secondary">发言稿</div>
          </div>
        )}
        <SpeechDraftView
          visible={showDraftView}
          onToggleSpeechDraft={handleToggleSpeechDraft}
          showDraftView={showDraftView}
          streamName={originStreamName}
          isFromList={isFromList}
        />
        {connectState === ConnectorState.Init ? (
          <LinkView
            streamName={originStreamName}
            onConnectSuccess={handleConnectSuccessWrapper}
            loading={linkLoading}
          />
        ) : connectState === ConnectorState.UnStart ? (
          <ControlStartView
            originStreamName={originStreamName}
            onStartRecordSuccess={handleStartRecordSuccess}
            deviceSn={deviceSn}
          />
        ) : (
          <CollaborationView
            streamName={streamName || originStreamName}
            onShowJumpSummaryDialog={handleShowJumpSummaryDialog}
          />
        )}

        {/* 统一的对话框组件 */}
        <CommonDialog ref={commonDialogRef} />

        <IPConnectModal
          ref={ipConnectModalRef}
          streamName={streamName || originStreamName}
          showTrigger={true}
          handleConnectByCode={handleConnectByCode}
          onConnectSuccess={handleConnectSuccessWrapper}
          onClose={handleModalClose}
        />
        <CodeConnectModal
          ref={codeConnectModalRef}
          streamName={streamName || originStreamName}
          handleConnectByIp={handleConnectByIp}
          onConnectSuccess={handleConnectSuccessWrapper}
          onClose={handleModalClose}
        />
      </div>
    </div>
  );
};

export default SimulInter;
